import * as Sentry from "@sentry/browser";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { isOnNodeJS, isSentryEnabled } from "common/utils/context";
import { productLog } from "common/utils/logging";
import { getWindowPersistentValue, setWindowPersistentValue } from "common/utils/persistentValue";
import { formatBytes } from "./MeshDataStats";
import { WebGLDebugUtils } from "./webgl-debug";

const MEMORY_HISTORY_KEEP_MINUTES = 10;
const MEMORY_HISTORY_INTERVAL_MS = 3000;

// Sentry allows at most 20MB for a compressed request, and at most 100MB of uncompressed attachments per event
// https://docs.sentry.io/platforms/javascript/enriching-events/attachments/
const CONSOLE_MESSAGE_BUFFER_SIZE_BYTES = 1024 * 1024; // 1MB

// Work around eslint no-console rule
function yesConsole() {
  return console;
}

// A rolling buffer of console messages that we upload to Sentry.
class ConsoleMessageBuffer {
  private sizeLimitBytes: number;
  constructor(sizeLimitBytes: number) {
    this.sizeLimitBytes = sizeLimitBytes;
  }

  private messages: string[] = [];

  addMessage(message: string, level: "log" | "error" | "warn") {
    // prepend with a date/time stamp
    this.messages.push(`${new Date().toLocaleString()} ${level}: ${message}`);

    if (this.messages.join("").length > this.sizeLimitBytes) {
      this.messages.shift();
    }
  }

  getMessages() {
    return this.messages;
  }

  // hook into console.log, console.error, and console.warn
  private originalConsoleLog = yesConsole().log;
  private originalConsoleError = yesConsole().error;
  private originalConsoleWarn = yesConsole().warn;

  install() {
    yesConsole().log = this.log;
    yesConsole().error = this.error;
    yesConsole().warn = this.warn;
  }

  uninstall() {
    yesConsole().log = this.originalConsoleLog;
    yesConsole().error = this.originalConsoleError;
    yesConsole().warn = this.originalConsoleWarn;
  }

  private log = (...args: any[]) => {
    this.addMessage(args.join(" "), "log");
    this.originalConsoleLog(...args);
  };

  private error = (...args: any[]) => {
    this.addMessage(args.join(" "), "error");
    this.originalConsoleError(...args);
  };

  private warn = (...args: any[]) => {
    this.addMessage(args.join(" "), "warn");
    this.originalConsoleWarn(...args);
  };
}

// This class will hook into any call to "loseContext" and store a stack trace
class LoseContextListener {
  private stackTrace: string | undefined;

  private originalGetContext: ((contextType: string, contextAttributes?: any) => any) | undefined;

  private handleGetExtension(originalGetExtension: (extensionName: string) => any, self: LoseContextListener) {
    return function (extensionName: string) {
      const extension = originalGetExtension.call(this, extensionName);
      if (extension && extension.loseContext) {
        const originalLoseContext = extension.loseContext;
        extension.loseContext = () => {
          self.stackTrace = new Error().stack;
          productLog("loseContext stack trace:", self.stackTrace);
          originalLoseContext.call(extension);
        };
      }
      return extension;
    };
  }

  install(): void {
    if (isOnNodeJS()) {
      return;
    }
    // Hook into getCanvas, then return an instrumented context for webgl and webgl2
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    this.originalGetContext = HTMLCanvasElement.prototype.getContext;
    HTMLCanvasElement.prototype.getContext = function (contextType: string, contextAttributes?: any) {
      assertIsNotUndefined(self.originalGetContext);
      // Return an instrumented context
      if (contextType === "webgl" || contextType === "webgl2") {
        const context = self.originalGetContext.call(this, contextType, contextAttributes) as
          | null
          | WebGLRenderingContext
          | WebGL2RenderingContext;
        if (context) {
          // Override the getExtension method to return a new class that overrides loseContext
          const originalGetExtension = context.getExtension;
          const newGetExtension = self.handleGetExtension(originalGetExtension, self);
          context.getExtension = newGetExtension;
        }
        // Return the instrumented context
        return context;
      }
      return self.originalGetContext.call(this, contextType, contextAttributes);
    } as (contextType: string, contextAttributes?: any) => any;
  }

  getStackTrace(): string | undefined {
    const stackTrace = this.stackTrace;
    this.stackTrace = undefined;
    return stackTrace;
  }

  uninstall(): void {
    if (this.originalGetContext) {
      HTMLCanvasElement.prototype.getContext = this.originalGetContext;
    }
  }
}

export interface MemoryUsageHistory {
  timestamp: number[];

  liveContexts: number[];
  totalMemoryUsage: number[];

  liveBuffers: number[];
  liveTextures: number[];
  livePrograms: number[];
  linkProgramCalls: number[];
  memoryUsageBuffers: number[];
  memoryUsageTextures: number[];
}

function bytesPerPixel(gl: WebGL2RenderingContext, internalformat: number) {
  switch (internalformat) {
    case gl.RGBA32F:
    case gl.RGB32F:
      return 16;

    case gl.RGBA16F:
    case gl.RGB16F:
    case gl.RG32F:
      return 8;

    case gl.DEPTH32F_STENCIL8:
      return 5;

    // 32-bit formats (4 bytes)
    case gl.RGBA:
    case gl.RGBA8:
    case gl.SRGB8_ALPHA8:
    case gl.RGB: // Really stored as RGBA under the hood
    case gl.RGB8:
    case gl.SRGB8:
    case gl.RG16F:
    case gl.R32F:
    case gl.DEPTH24_STENCIL8:
    case gl.DEPTH_COMPONENT:
    case gl.DEPTH_STENCIL:
      return 4;

    case gl.DEPTH_COMPONENT24:
      return 3;

    // 16-bit formats (2 bytes)
    case gl.RG:
    case gl.RG8:
    case gl.R16F:
    case gl.DEPTH_COMPONENT16:
      return 2;

    // 8-bit formats (1 byte)
    case gl.RED:
    case gl.R8:
    case gl.STENCIL_INDEX8:
      return 1;

    default:
      WebGLDebugUtils.init(gl);
      console.warn("Did not recognize GL texture internalformat" + WebGLDebugUtils.glEnumToString(internalformat));
      return 0;
  }
}

const PersistentVarName = "higharc-webgl-resource-tracker";

export class WebGLMultiContextResourceTracker {
  static instance: WebGLMultiContextResourceTracker | null = null;

  private readonly trackers: WebGLContextResourceTracker[] = [];

  private readonly memoryUsageHistory: MemoryUsageHistory;

  private totalMemoryUsage = 0;
  private peakMemoryUsage = 0;

  private commitInterval: NodeJS.Timeout | undefined;

  private consoleMessageBuffer: ConsoleMessageBuffer | undefined;
  private loseContextListener: LoseContextListener | undefined;

  constructor() {
    const priorInstance = getWindowPersistentValue<WebGLMultiContextResourceTracker | undefined>(
      PersistentVarName,
      undefined,
    );
    if (priorInstance) {
      priorInstance.shutdown();
    }

    this.memoryUsageHistory = {
      timestamp: [],
      liveContexts: [],
      totalMemoryUsage: [],
      liveBuffers: [],
      liveTextures: [],
      livePrograms: [],
      linkProgramCalls: [],
      memoryUsageBuffers: [],
      memoryUsageTextures: [],
    };

    // Only capture console messages if Sentry is enabled.
    if (isSentryEnabled() && !isOnNodeJS()) {
      this.consoleMessageBuffer = new ConsoleMessageBuffer(CONSOLE_MESSAGE_BUFFER_SIZE_BYTES);
      this.consoleMessageBuffer.install();

      this.loseContextListener = new LoseContextListener();
      this.loseContextListener.install();
    }

    this.commitInterval = setInterval(() => {
      this.commitMemoryUsageSample();
    }, MEMORY_HISTORY_INTERVAL_MS);
  }

  static addContext(gl: WebGLRenderingContext | WebGL2RenderingContext, label: string, gpuVendorInfo: any) {
    if (!this.instance) {
      this.instance = new WebGLMultiContextResourceTracker();
      setWindowPersistentValue(PersistentVarName, this.instance);
    }

    const contextLostListener = (e: Event) => {
      const errorMessage = "WebGL Context lost in context: " + label;
      productLog(errorMessage);
      e.preventDefault();
      const stackTrace = this.instance?.loseContextListener?.getStackTrace();
      const contextLostInfo = {
        gpuInfo: JSON.stringify(gpuVendorInfo),
        contextMemoryReport: tracker.reportJSON(),
        totalGlContexts: this.instance?.trackers.length ?? 0,
        totalGlMemory: formatBytes(this.instance?.totalMemoryUsage ?? 0),
        peakTotalGlMemory: formatBytes(this.instance?.peakMemoryUsage ?? 0),
      };
      // Trigger a capture in 1 second, to allow more console messages to be collected.
      setTimeout(() => {
        Sentry.getCurrentScope().clearAttachments();
        if (this.instance?.consoleMessageBuffer) {
          Sentry.getCurrentScope().addAttachment({
            filename: "console_messages.txt",
            data: this.instance.consoleMessageBuffer.getMessages().join("\n"),
          });
        }
        Sentry.getCurrentScope().setTag("higharc-webgl-lost-context-label", label);
        const error = new Error(errorMessage);
        // Setting the stack property makes Sentry process the stack trace.
        error.stack = stackTrace;
        Sentry.captureException(error, {
          extra: contextLostInfo,
        });
      }, 1000);
      if (this.instance) {
        this.instance.commitMemoryUsageSample();
        const lostContextIndex = this.instance.trackers.indexOf(tracker);
        if (lostContextIndex !== -1) {
          this.instance.trackers.splice(lostContextIndex, 1);
        }
        this.instance.totalMemoryUsage = this.instance.computeTotalMemoryUsageFromTrackers();
      }
    };

    const tracker = new WebGLContextResourceTracker(gl, label, this.instance, contextLostListener);

    const existingTracker = this.instance.trackers.find((t) => t.tracksContext(gl));
    if (existingTracker) {
      console.warn(`WebGL Resource Tracker duplicate! Old label: ${existingTracker.label}, new label: ${label}`);
    } else {
      productLog(`WebGL Resource Tracker is now tracking context: ${label}`);
    }

    this.instance.trackers.push(tracker);

    gl.canvas.addEventListener("webglcontextlost", contextLostListener);
    return tracker;
  }

  static removeContext(gl: WebGLRenderingContext | WebGL2RenderingContext) {
    if (!this.instance) {
      return;
    }
    const trackerIndex = this.instance.trackers.findIndex((t) => t.tracksContext(gl));
    if (trackerIndex !== -1) {
      const tracker = this.instance.trackers[trackerIndex];
      productLog(`Removing context ${tracker.label} from tracker`);
      tracker.removeContextLostListener();
      this.instance.trackers.splice(trackerIndex, 1);
      this.instance.memoryUsageChanged(-tracker.getMemoryUsageBuffers());
      this.instance.memoryUsageChanged(-tracker.getMemoryUsageTextures());
    }
  }

  memoryUsageChanged(diff: number) {
    this.totalMemoryUsage += diff;
    this.peakMemoryUsage = Math.max(this.peakMemoryUsage, this.totalMemoryUsage);

    // Update the last entry in history so that short-lived spikes will be included in the record.
    if (
      this.memoryUsageHistory.totalMemoryUsage.length > 0 &&
      this.totalMemoryUsage >
        this.memoryUsageHistory.totalMemoryUsage[this.memoryUsageHistory.totalMemoryUsage.length - 1]
    ) {
      this.memoryUsageHistory.totalMemoryUsage[this.memoryUsageHistory.totalMemoryUsage.length - 1] =
        this.totalMemoryUsage;
    }
  }

  getTotalMemoryUsage() {
    return this.totalMemoryUsage;
  }

  getPeakMemoryUsage() {
    return this.peakMemoryUsage;
  }

  getHistory() {
    return this.memoryUsageHistory;
  }

  private computeTotalMemoryUsageFromTrackers(): number {
    let totalMemoryUsageBuffers = 0;
    let totalMemoryUsageTextures = 0;
    for (const tracker of this.trackers) {
      totalMemoryUsageBuffers += tracker.getMemoryUsageBuffers();
      totalMemoryUsageTextures += tracker.getMemoryUsageTextures();
    }
    return totalMemoryUsageBuffers + totalMemoryUsageTextures;
  }

  commitMemoryUsageSample() {
    let liveBuffers = 0;
    let liveTextures = 0;
    let livePrograms = 0;
    let linkProgramCalls = 0;
    let memoryUsageBuffers = 0;
    let memoryUsageTextures = 0;
    for (const tracker of this.trackers) {
      liveBuffers += tracker.getLiveBuffers();
      liveTextures += tracker.getLiveTextures();
      livePrograms += tracker.getLivePrograms();
      linkProgramCalls += tracker.getLinkProgramCalls();
      memoryUsageBuffers += tracker.getMemoryUsageBuffers();
      memoryUsageTextures += tracker.getMemoryUsageTextures();
    }
    this.memoryUsageHistory.timestamp.push(Date.now());
    this.memoryUsageHistory.liveContexts.push(this.trackers.length);
    this.memoryUsageHistory.totalMemoryUsage.push(this.totalMemoryUsage);
    this.memoryUsageHistory.liveBuffers.push(liveBuffers);
    this.memoryUsageHistory.liveTextures.push(liveTextures);
    this.memoryUsageHistory.livePrograms.push(livePrograms);
    this.memoryUsageHistory.linkProgramCalls.push(linkProgramCalls);
    this.memoryUsageHistory.memoryUsageBuffers.push(memoryUsageBuffers);
    this.memoryUsageHistory.memoryUsageTextures.push(memoryUsageTextures);

    if (
      this.memoryUsageHistory.timestamp.length >
      (MEMORY_HISTORY_KEEP_MINUTES * 60 * 1000) / MEMORY_HISTORY_INTERVAL_MS
    ) {
      this.memoryUsageHistory.timestamp.splice(0, 1);
      this.memoryUsageHistory.liveContexts.splice(0, 1);
      this.memoryUsageHistory.totalMemoryUsage.splice(0, 1);
      this.memoryUsageHistory.liveBuffers.splice(0, 1);
      this.memoryUsageHistory.liveTextures.splice(0, 1);
      this.memoryUsageHistory.livePrograms.splice(0, 1);
      this.memoryUsageHistory.linkProgramCalls.splice(0, 1);
      this.memoryUsageHistory.memoryUsageBuffers.splice(0, 1);
      this.memoryUsageHistory.memoryUsageTextures.splice(0, 1);
    }
  }

  static report() {
    if (!this.instance) {
      return "";
    }

    return `Total memory usage across all WebGL contexts: ${formatBytes(
      this.instance.computeTotalMemoryUsageFromTrackers(),
    )}

    ${this.instance.trackers.map((t) => t.report()).join("\n")}`;
  }

  shutdown() {
    if (this.commitInterval) {
      clearInterval(this.commitInterval);
    }
    if (this.consoleMessageBuffer) {
      this.consoleMessageBuffer.uninstall();
    }
    if (this.loseContextListener) {
      this.loseContextListener.uninstall();
    }
    setWindowPersistentValue(PersistentVarName, undefined);
  }

  static shutdown() {
    if (this.instance) {
      this.instance.shutdown();
      this.instance = null;
    }
  }
}

/**
 * A low-overhead method to track the memory usage of WebGL resources in a single WebGL context.
 * Selected functions on the WebGL2RenderingContext are overridden to insert accounting of resources.
 *
 * We track the number of times resources are allocated and the total current memory use.
 *
 * The reported values should be taken as an estimate.
 *
 * Does not support compressed textures.
 */
export default class WebGLContextResourceTracker {
  private createBufferCalls: number = 0;
  private liveBuffers: number = 0;

  private createTextureCalls: number = 0;
  private liveTextures: number = 0;

  private createRenderbufferCalls: number = 0;
  private liveRenderbuffers: number = 0;

  private createFramebufferCalls: number = 0;
  private liveFramebuffers: number = 0;

  private createProgramCalls: number = 0;
  private livePrograms: number = 0;

  private compileShaderCalls: number = 0;
  private linkProgramCalls: number = 0;

  private memoryUsageBuffers: number = 0;
  private memoryUsageTextures: number = 0;

  private peakMemoryUsageBuffers: number = 0;
  private peakMemoryUsageTextures: number = 0;

  getMemoryUsageBuffers() {
    return this.memoryUsageBuffers;
  }

  getMemoryUsageTextures() {
    return this.memoryUsageTextures;
  }

  getPeakMemoryUsageBuffers() {
    return this.peakMemoryUsageBuffers;
  }

  getPeakMemoryUsageTextures() {
    return this.peakMemoryUsageTextures;
  }

  getLiveBuffers() {
    return this.liveBuffers;
  }

  getLiveTextures() {
    return this.liveTextures;
  }

  getLiveFramebuffers() {
    return this.liveFramebuffers;
  }

  getLivePrograms() {
    return this.livePrograms;
  }

  getLinkProgramCalls() {
    return this.linkProgramCalls;
  }

  tracksContext(gl: WebGLRenderingContext | WebGL2RenderingContext) {
    return this.gl === gl;
  }

  private readonly bufferToMemory: WeakMap<WebGLBuffer, number> | Map<WebGLBuffer, number>;

  // Map from textures to mip level specific memory usage.
  private readonly textureToLevelsMemory:
    | WeakMap<WebGLTexture | WebGLRenderbuffer, number[]>
    | Map<WebGLTexture | WebGLRenderbuffer, number[]>;

  constructor(
    private readonly gl: WebGLRenderingContext | WebGL2RenderingContext,
    public readonly label: string,
    private readonly multiContextTracker: WebGLMultiContextResourceTracker,
    private readonly contextLostListener?: (e: Event) => void,
  ) {
    if (this.contextLostListener) {
      this.gl.canvas.addEventListener("webglcontextlost", this.contextLostListener);
    }
    if (isOnNodeJS()) {
      // Headless GL uses numbers as buffer and texture IDs, so we need to use a regular map instead of a WeakMap, which requires keys to be objects.
      this.bufferToMemory = new Map();
      this.textureToLevelsMemory = new Map();
    } else {
      this.bufferToMemory = new WeakMap();
      this.textureToLevelsMemory = new WeakMap();
    }

    const originalCreateBuffer = this.gl.createBuffer.bind(this.gl);
    this.gl.createBuffer = () => {
      ++this.createBufferCalls;
      ++this.liveBuffers;
      return originalCreateBuffer();
    };

    const originalDeleteBuffer = this.gl.deleteBuffer.bind(this.gl);
    this.gl.deleteBuffer = (buffer) => {
      // This assumes that the delete call actually succeeds, and doesn't take delayed deletion into account.
      // Same goes for the functions below.
      --this.liveBuffers;
      this.removeBufferMemoryUsage(buffer);
      return originalDeleteBuffer(buffer);
    };

    const originalBufferData = this.gl.bufferData.bind(this.gl);
    this.gl.bufferData = (target, sizeOrSrc, usage) => {
      const size = typeof sizeOrSrc === "number" ? sizeOrSrc : sizeOrSrc.byteLength;
      if (size >= 0) {
        if (target === this.gl.ARRAY_BUFFER) {
          const buffer = this.gl.getParameter(this.gl.ARRAY_BUFFER_BINDING);
          this.updateBufferMemoryUsage(buffer, size);
        } else if (target === this.gl.ELEMENT_ARRAY_BUFFER) {
          const buffer = this.gl.getParameter(this.gl.ELEMENT_ARRAY_BUFFER_BINDING);
          this.updateBufferMemoryUsage(buffer, size);
        }
      }
      return originalBufferData(target, sizeOrSrc, usage);
    };

    const originalCreateTexture = this.gl.createTexture.bind(this.gl);
    this.gl.createTexture = () => {
      ++this.createTextureCalls;
      ++this.liveTextures;
      return originalCreateTexture();
    };

    const originalDeleteTexture = this.gl.deleteTexture.bind(this.gl);
    this.gl.deleteTexture = (texture) => {
      --this.liveTextures;
      this.removeTextureMemoryUsage(texture);
      return originalDeleteTexture(texture);
    };

    const originalTexImage2D = this.gl.texImage2D;
    this.gl.texImage2D = (...args) => {
      const level = args[1];
      const target = args[0];
      const texture = this.getBoundTexture(target);
      const internalformat = args[2];
      if (args.length >= 9) {
        // target, level, internalformat, width, height, border, format, type, pixels
        // or
        // target, level, internalformat, width, height, border, format, type, src, srcOffset
        const width = args[3];
        const height = args[4];
        if (width > 0 && height > 0) {
          const pixelCount = width * height;
          this.updateTextureMemoryUsage(texture, level, pixelCount, internalformat);
        }
      } else {
        // target, level, internalformat, format, type, pixels
        const pixels: TexImageSource = args[5];
        if (pixels instanceof ImageData || pixels instanceof HTMLImageElement || pixels instanceof HTMLCanvasElement) {
          const pixelCount = pixels.width * pixels.height;
          this.updateTextureMemoryUsage(texture, level, pixelCount, internalformat);
        }
      }
      return originalTexImage2D.apply(this.gl, args as any);
    };

    const originalCreateRenderbuffer = this.gl.createRenderbuffer.bind(this.gl);
    this.gl.createRenderbuffer = () => {
      ++this.createRenderbufferCalls;
      ++this.liveRenderbuffers;
      return originalCreateRenderbuffer();
    };

    const originalDeleteRenderbuffer = this.gl.deleteRenderbuffer.bind(this.gl);
    this.gl.deleteRenderbuffer = (renderbuffer) => {
      --this.liveRenderbuffers;
      this.removeTextureMemoryUsage(renderbuffer);
      return originalDeleteRenderbuffer(renderbuffer);
    };

    const originalRenderbufferStorage = this.gl.renderbufferStorage.bind(this.gl);
    this.gl.renderbufferStorage = (target, internalformat, width, height) => {
      if (width > 0 && height > 0) {
        const renderbuffer = this.gl.getParameter(this.gl.RENDERBUFFER_BINDING);
        const pixelCount = width * height;
        this.updateTextureMemoryUsage(renderbuffer, 0, pixelCount, internalformat);
      }
      return originalRenderbufferStorage(target, internalformat, width, height);
    };

    // The getParameter check is to avoid exceptions when testing with WebGL 2.0 disabled in Firefox.
    // The version string is standardized in the WebGL spec.
    if (this.gl.getParameter(this.gl.VERSION).startsWith("WebGL 2.0") && this.gl instanceof WebGL2RenderingContext) {
      const originalRenderBufferStorageMultisample = this.gl.renderbufferStorageMultisample.bind(this.gl);
      this.gl.renderbufferStorageMultisample = (target, samples, internalformat, width, height) => {
        if (width > 0 && height > 0) {
          const renderbuffer = this.gl.getParameter(this.gl.RENDERBUFFER_BINDING);
          const pixelCount = width * height * Math.max(1, samples);
          this.updateTextureMemoryUsage(renderbuffer, 0, pixelCount, internalformat);
        }
        return originalRenderBufferStorageMultisample(target, samples, internalformat, width, height);
      };

      const originalTexImage3D = this.gl.texImage3D;
      this.gl.texImage3D = (...args) => {
        const level = args[1];
        const target = args[0];
        const internalformat = args[2];
        const width = args[3];
        const height = args[4];
        const depth = args[5];
        if (width > 0 && height > 0 && depth > 0) {
          const texture = this.getBoundTexture(target);
          const pixelCount = width * height * depth;
          this.updateTextureMemoryUsage(texture, level, pixelCount, internalformat);
        }
        return originalTexImage3D.apply(this.gl, args as any);
      };

      const originalTexStorage2D = this.gl.texStorage2D.bind(this.gl);
      this.gl.texStorage2D = (target, levels, internalformat, width, height) => {
        if (width > 0 && height > 0) {
          const texture = this.getBoundTexture(target);
          for (let level = 0; level < levels; level++) {
            const levelWidth = Math.max(1, width >> level);
            const levelHeight = Math.max(1, height >> level);
            let pixelCount = levelWidth * levelHeight;
            if (target === this.gl.TEXTURE_CUBE_MAP) {
              pixelCount *= 6;
            }
            this.updateTextureMemoryUsage(texture, level, pixelCount, internalformat);
          }
        }
        return originalTexStorage2D(target, levels, internalformat, width, height);
      };

      const originalTexStorage3D = this.gl.texStorage3D.bind(this.gl);
      this.gl.texStorage3D = (target, levels, internalformat, width, height, depth) => {
        const texture = this.getBoundTexture(target);
        if (width > 0 && height > 0 && depth > 0) {
          for (let level = 0; level < levels; level++) {
            const levelWidth = Math.max(1, width >> level);
            const levelHeight = Math.max(1, height >> level);
            const levelDepth =
              target === (this.gl as WebGL2RenderingContext).TEXTURE_3D ? Math.max(1, depth >> level) : depth;
            const pixelCount = levelWidth * levelHeight * levelDepth;
            this.updateTextureMemoryUsage(texture, level, pixelCount, internalformat);
          }
        }
        return originalTexStorage3D(target, levels, internalformat, width, height, depth);
      };
    }

    const originalCreateFramebuffer = this.gl.createFramebuffer.bind(this.gl);
    this.gl.createFramebuffer = () => {
      ++this.createFramebufferCalls;
      ++this.liveFramebuffers;
      return originalCreateFramebuffer();
    };

    const originalDeleteFramebuffer = this.gl.deleteFramebuffer.bind(this.gl);
    this.gl.deleteFramebuffer = (framebuffer) => {
      --this.liveFramebuffers;
      return originalDeleteFramebuffer(framebuffer);
    };

    const originalCreateProgram = this.gl.createProgram.bind(this.gl);
    this.gl.createProgram = () => {
      ++this.createProgramCalls;
      ++this.livePrograms;
      return originalCreateProgram();
    };

    const originalDeleteProgram = this.gl.deleteProgram.bind(this.gl);
    this.gl.deleteProgram = (program) => {
      --this.livePrograms;
      return originalDeleteProgram(program);
    };

    const originalCompileShader = this.gl.compileShader.bind(this.gl);
    this.gl.compileShader = (shader) => {
      ++this.compileShaderCalls;
      return originalCompileShader(shader);
    };

    const originalLinkProgram = this.gl.linkProgram.bind(this.gl);
    this.gl.linkProgram = (program) => {
      ++this.linkProgramCalls;
      return originalLinkProgram(program);
    };
  }

  removeContextLostListener() {
    if (this.contextLostListener) {
      this.gl.canvas.removeEventListener("webglcontextlost", this.contextLostListener);
    }
  }

  reportJSON() {
    return {
      label: this.label,
      liveBuffers: this.liveBuffers,
      createBufferCalls: this.createBufferCalls,
      liveTextures: this.liveTextures,
      createTextureCalls: this.createTextureCalls,
      liveRenderbuffers: this.liveRenderbuffers,
      createRenderbufferCalls: this.createRenderbufferCalls,
      liveFramebuffers: this.liveFramebuffers,
      createFramebufferCalls: this.createFramebufferCalls,
      livePrograms: this.livePrograms,
      createProgramCalls: this.createProgramCalls,
      compileShaderCalls: this.compileShaderCalls,
      linkProgramCalls: this.linkProgramCalls,
      memoryUsageBuffers: formatBytes(this.memoryUsageBuffers),
      peakMemoryUsageBuffers: formatBytes(this.peakMemoryUsageBuffers),
      memoryUsageTextures: formatBytes(this.memoryUsageTextures),
      peakMemoryUsageTextures: formatBytes(this.peakMemoryUsageTextures),
      totalMemoryUsage: formatBytes(this.memoryUsageBuffers + this.memoryUsageTextures),
    };
  }

  report() {
    return `${this.label} WebGL context:
    Live GL buffers: ${this.liveBuffers} createBuffer calls: ${this.createBufferCalls}
    Live GL textures: ${this.liveTextures} createTexture calls: ${this.createTextureCalls}
    Live GL framebuffers: ${this.liveFramebuffers} createFramebuffer calls: ${this.createFramebufferCalls}
    Live GL programs: ${this.livePrograms} createProgram calls: ${this.createProgramCalls} compileShader calls: ${
      this.compileShaderCalls
    } linkProgram calls: ${this.linkProgramCalls}
    Buffer memory usage: ${formatBytes(this.memoryUsageBuffers)} peak: ${formatBytes(this.peakMemoryUsageBuffers)}
    Texture and renderbuffer memory usage: ${formatBytes(this.memoryUsageTextures)} peak: ${formatBytes(
      this.peakMemoryUsageTextures,
    )}
    Total estimated memory usage: ${formatBytes(this.memoryUsageBuffers + this.memoryUsageTextures)}
    `;
  }

  private removeBufferMemoryUsage(buffer: WebGLBuffer | null) {
    if (buffer && this.bufferToMemory.has(buffer)) {
      const diff = -(this.bufferToMemory.get(buffer) ?? 0);
      this.memoryUsageBuffers += diff;
      this.multiContextTracker.memoryUsageChanged(diff);
      this.bufferToMemory.delete(buffer);
    }
  }

  private updateBufferMemoryUsage(buffer: WebGLBuffer | null, size: number) {
    if (buffer) {
      const oldSize = this.bufferToMemory.get(buffer) ?? 0;
      const diff = size - oldSize;
      this.memoryUsageBuffers += diff;
      this.peakMemoryUsageBuffers = Math.max(this.peakMemoryUsageBuffers, this.memoryUsageBuffers);
      this.multiContextTracker.memoryUsageChanged(diff);
      this.bufferToMemory.set(buffer, size);
    }
  }

  private removeTextureMemoryUsage(object: WebGLTexture | WebGLRenderbuffer | null) {
    if (object && this.textureToLevelsMemory.has(object)) {
      const levelsMemory = this.textureToLevelsMemory.get(object) ?? [];
      const diff = -levelsMemory.reduce((a, b) => a + b, 0);
      this.memoryUsageTextures += diff;
      this.multiContextTracker.memoryUsageChanged(diff);
      this.textureToLevelsMemory.delete(object);
    }
  }

  private updateTextureMemoryUsage(
    object: WebGLTexture | WebGLRenderbuffer | null,
    level: number,
    pixelCount: number,
    internalformat: number,
  ) {
    if (object) {
      const size = pixelCount * bytesPerPixel(this.gl as WebGL2RenderingContext, internalformat);
      const oldSize = this.textureToLevelsMemory.get(object)?.[level] ?? 0;
      const diff = size - oldSize;
      this.memoryUsageTextures += diff;
      this.peakMemoryUsageTextures = Math.max(this.peakMemoryUsageTextures, this.memoryUsageTextures);
      this.multiContextTracker.memoryUsageChanged(diff);
      const levelsMemory = this.textureToLevelsMemory.get(object) ?? [];
      levelsMemory[level] = size;
      this.textureToLevelsMemory.set(object, levelsMemory);
    }
  }

  private getBoundTexture(target: number): WebGLTexture | null {
    if (target === this.gl.TEXTURE_2D) {
      return this.gl.getParameter(this.gl.TEXTURE_BINDING_2D);
    } else if (target === this.gl.TEXTURE_CUBE_MAP) {
      return this.gl.getParameter(this.gl.TEXTURE_BINDING_CUBE_MAP);
    } else if (target === (this.gl as WebGL2RenderingContext).TEXTURE_3D) {
      return this.gl.getParameter((this.gl as WebGL2RenderingContext).TEXTURE_BINDING_3D);
    } else if (target === (this.gl as WebGL2RenderingContext).TEXTURE_2D_ARRAY) {
      return this.gl.getParameter((this.gl as WebGL2RenderingContext).TEXTURE_BINDING_2D_ARRAY);
    }
    return null;
  }
}
